import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Scrollbar,
  SearchInput,
  Space,
  Submenu,
  SubmenuList,
  SubmenuToggle,
  Text,
} from '@tonic-one/react';
import { AngleDownIcon, AngleRightIcon } from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import { noop } from '@tonic-one/utils';
import debounce from 'lodash/debounce';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import CSpinner from 'src/components/CSpinner';
import IconButton from 'src/components/IconButton';
import {
  CollaborationPolicyNameEnum,
  MailPolicyNameEnum,
  OverAllPolicyType,
  PolicyItem,
  PolicyItemInTable,
  PolicyListModalName,
  PolicyListModalState,
  ServiceCollection,
} from 'src/pages/Policy/utils/types';

import { useOrgStore } from '../../../models/useOrgModel';
import {
  COLLABORATION_POLICY_TYPE_ID_NAME_MAP,
  MAIL_POLICY_TYPE_ID_NAME_MAP,
  PolicyTypeEnum,
} from '../utils/constants';
import { checkManualScanPending } from '../utils/fns';
import { DeleteModal, ScanModal } from './Modal';
import PolicyListTable from './PolicyListTable';
import { ScanningModal } from './Modal/scanning';
import CheckSkippedModal from '@Policy/OverAllPolicyList/Modal/checkSkipped';
import { handleNavigate } from '@Utils/common';
import GmailPolicyEnableModal from './Modal/gmailPolicyEnable';
import { useGlobalSettingStore } from '../../../models/useGlobalSettingModel';
import { useLayoutStore } from '@Models/useLayoutModel';
import { CorrelatedTemplateUrl } from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/utils';
import {
  OverAllPolicyTypeContext,
  PolicyManualScanRunningQueue,
} from './context/OverAllPolicyTypeContext';
import customConfig from 'src/config';
import { usePolicyListQuery } from './hooks/usePolicyListQuery';
import { usePolicyStatusMutation } from './hooks/usePolicyStatusMutation';
import { usePolicyReorderMutation } from './hooks/usePolicyReorderMutation';
import { PolicyMutationProvider } from './context/PolicyMutationContext';

const CollectionAndServicesMap: { [key in ServiceCollection]: number[] } = {
  mail: customConfig.ui.policy.policyTemplateTypes.emailTypes || [
    1, 82, 21, 22,
  ],
  collaboration: customConfig.ui.policy.policyTemplateTypes
    .collaborationTypes || [2, 3, 4, 5, 6, 9, 10, 41, 42],
};

export const PolicyList = () => {
  const overAllPolicyType = useContext(OverAllPolicyTypeContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { orgId } = useOrgStore();
  const { layoutHeight } = useLayoutStore();
  const [keyWord, setKeyword] = useState('');
  const debounceSetKeyWord = debounce(setKeyword, 200);
  const [refreshTableTrigger, setRefreshTableTrigger] = useState<Date | null>(
    null
  );
  const [policyManualScanRunningQueue, setPolicyManualScanRunningQueue] =
    useState<PolicyManualScanRunningQueue>({});
  const [modal, setModal] = useState<PolicyListModalState>({
    name: PolicyListModalName.None,
    policy: null,
  });
  const [policyId4ManualScan, setPolicyId4ManualScan] = useState<string>('');
  const timeRef = useRef<any>(null);

  const typeInParam = useParams()['collectionType'];
  let collectionType: ServiceCollection = ServiceCollection.Mail;
  if (
    typeInParam &&
    (Object.values(ServiceCollection) as string[]).includes(typeInParam)
  ) {
    collectionType = typeInParam as ServiceCollection;
  }

  const { policyLists, policyTypes, isPending, error, refetch } =
    usePolicyListQuery(overAllPolicyType, collectionType, orgId);

  // Initialize mutation hooks for optimistic updates with loading tracking
  const statusMutation = usePolicyStatusMutation({
    overAllPolicyType,
    collectionType,
    orgId,
  });

  const reorderMutation = usePolicyReorderMutation({
    overAllPolicyType,
    collectionType,
    orgId,
  });

  const checkPolicyManualScanRunningQueue = (
    policyItem: PolicyItem | PolicyItemInTable
  ) => {
    if (
      policyItem.policyType &&
      policyItem.taskState &&
      checkManualScanPending(policyItem.taskState)
    ) {
      policyManualScanRunningQueue[policyItem.policyType] =
        policyItem.policyUuid;
      setPolicyManualScanRunningQueue(policyManualScanRunningQueue);
    }

    if (
      Object.keys(policyManualScanRunningQueue).length > 0 &&
      policyItem.taskState &&
      !checkManualScanPending(policyItem.taskState) &&
      policyItem.policyType in policyManualScanRunningQueue &&
      policyManualScanRunningQueue[policyItem.policyType] ===
        policyItem.policyUuid
    ) {
      setPolicyId4ManualScan(
        policyManualScanRunningQueue[policyItem.policyType]
      );
      delete policyManualScanRunningQueue[policyItem.policyType];
      setPolicyManualScanRunningQueue(policyManualScanRunningQueue);
      refreshTable4ManualScan();

      if (Object.keys(policyManualScanRunningQueue).length === 0) {
        clearInterval(timeRef.current);
        return;
      }
    }
  };

  const waitingForPolicyManualScanRunningQueue = async () => {
    if (Object.keys(policyManualScanRunningQueue).length === 0) {
      clearInterval(timeRef.current);
      return;
    }
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      refetch();
    }, 10000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  const refreshTable4ManualScan = async () => {
    refetch();
    setRefreshTableTrigger(new Date());
  };

  useEffect(() => {
    if (policyLists) {
      Object.values(policyLists)
        .flat()
        .forEach((item) => {
          checkPolicyManualScanRunningQueue(item);
        });
      waitingForPolicyManualScanRunningQueue();
    }
  }, [policyLists]);

  useEffect(() => {
    if (error) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    }
  }, [error, t]);

  useEffect(() => {
    if (refreshTableTrigger) {
      refetch();
    }
  }, [refreshTableTrigger, refetch]);

  if (orgId === 'all') {
    return <></>;
  }

  return (
    <PolicyMutationProvider
      value={{
        mutatingPolicyTypes: statusMutation.mutatingPolicyTypes,
        reorderingPolicyTypes: reorderMutation.reorderingPolicyTypes,
        statusMutation,
        reorderMutation,
      }}
    >
      <Flex height={layoutHeight} pt="24px" px="4x" flexDirection="column">
        <Flex gap="12px">
          <AddPolicyMenu
            policyTypes={policyTypes}
            collectionType={collectionType}
          />
          <SearchInput
            placeholder={t('policy.placeholder.searchPolicy')}
            onChange={(e: any) => {
              debounceSetKeyWord(e.target.value);
            }}
            onClearInput={() => {
              setKeyword('');
            }}
            width="374px"
          />
          {overAllPolicyType === OverAllPolicyType.ATP &&
            collectionType === ServiceCollection.Mail && (
              <Button
                variant="secondary"
                onClick={() => {
                  navigate(CorrelatedTemplateUrl.rules);
                  handleNavigate(CorrelatedTemplateUrl.rules);
                }}
              >
                <Text>{t('globalSetting.correlatedRules.goTo')}</Text>
              </Button>
            )}
          {overAllPolicyType === OverAllPolicyType.DLP && (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  navigate('/policy/data-loss-prevention/compliance-templates');
                }}
              >
                <Text>{t('policy.button.complianceTemplates')}</Text>
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  navigate('/policy/data-loss-prevention/data-identifiers');
                }}
              >
                <Text>{t('policy.button.dataIdentifiers')}</Text>
              </Button>
            </>
          )}
        </Flex>

        <Space height="24px" />
        {isPending ? (
          <CSpinner />
        ) : (
          <Scrollbar height="100%">
            {Object.entries(policyLists).map((v) => {
              const [policyName, list] = v;
              if (v[1].length === 0) {
                return null;
              }
              return (
                <PolicyListItem
                  keyWord={keyWord}
                  key={policyName}
                  policyName={
                    policyName as
                      | MailPolicyNameEnum
                      | CollaborationPolicyNameEnum
                  }
                  policyItems={list}
                  setModal={setModal}
                  setRefreshTableTrigger={setRefreshTableTrigger}
                  policyManualScanRunningQueue={policyManualScanRunningQueue}
                  overAllPolicyType={overAllPolicyType}
                  collectionType={collectionType}
                />
              );
            })}
          </Scrollbar>
        )}
        <DeleteModal
          modal={modal}
          setModal={setModal}
          setRefreshTableTrigger={setRefreshTableTrigger}
        />
        {modal.name === PolicyListModalName.Scan && (
          <ScanModal
            modal={modal}
            setModal={setModal}
            setRefreshTableTrigger={setRefreshTableTrigger}
          />
        )}
        {modal.name === PolicyListModalName.Scanning && (
          <ScanningModal
            policyId4ManualScan={policyId4ManualScan}
            setPolicyId4ManualScan={setPolicyId4ManualScan}
            modal={modal}
            setModal={setModal}
            setRefreshTableTrigger={setRefreshTableTrigger}
          />
        )}
        <CheckSkippedModal
          modal={modal}
          setModal={setModal}
          setRefreshTableTrigger={setRefreshTableTrigger}
        />
        <GmailPolicyEnableModal
          modal={modal}
          setModal={setModal}
          setRefreshTableTrigger={setRefreshTableTrigger}
        />
      </Flex>
    </PolicyMutationProvider>
  );
};

function AddPolicyMenu({
  policyTypes,
  collectionType,
}: {
  policyTypes: string[];
  collectionType: ServiceCollection;
}) {
  const overAllPolicyType = useContext(OverAllPolicyTypeContext);
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleClickMenuItem = (event: any) => {
    const value = event.target.getAttribute('value');
    const path =
      overAllPolicyType === OverAllPolicyType.ATP
        ? 'advanced-threat-protection'
        : 'data-loss-prevention';
    const url = `/policy/${path}/add-policy/${value}`;
    if (value) {
      handleNavigate(url);
      navigate(url);
    }
  };
  const policyMap =
    collectionType === ServiceCollection.Mail
      ? MAIL_POLICY_TYPE_ID_NAME_MAP
      : COLLABORATION_POLICY_TYPE_ID_NAME_MAP;
  return (
    <Menu height="32px">
      <MenuButton variant="primary">
        <Text>{t('policy.button.addPolicy')}</Text>
      </MenuButton>
      <MenuList width="max-content" onClick={handleClickMenuItem}>
        {CollectionAndServicesMap[collectionType].map((type) => {
          const v = policyMap[type];
          if (!v) {
            return null;
          }
          if (
            type === PolicyTypeEnum.GmailInline &&
            !selectedOrgGlobalSetting.gmailInlineFeatureShow
          ) {
            return null;
          }
          if (
            type === PolicyTypeEnum.ExchangeInline &&
            !selectedOrgGlobalSetting.exchangeInlineFeatureShow
          ) {
            return null;
          }
          if (typeof v === 'string') {
            return (
              <MenuItem value={v} key={v} disabled={!policyTypes.includes(v)}>
                {t(`policy.addPolicy.options.${v}`)}
              </MenuItem>
            );
          } else {
            const submenuKey = {
              22: 'gmailInline',
              82: 'exchangeInline',
            }[type as 22 | 82];
            return (
              <Submenu placement="right-start" key={JSON.stringify(v)}>
                <SubmenuToggle width="100%">
                  <MenuItem
                    onClick={noop}
                    disabled={
                      !(
                        policyTypes.includes(submenuKey + 'Outbound') ||
                        policyTypes.includes(submenuKey + 'Inbound')
                      )
                    }
                  >
                    <Flex
                      alignItems="center"
                      columnGap="2x"
                      justifyContent="space-between"
                      width="100%"
                    >
                      {t(`policy.addPolicy.options.${submenuKey}`)}
                      <Icon as={AngleRightIcon} />
                    </Flex>
                  </MenuItem>
                </SubmenuToggle>
                <SubmenuList width="max-content">
                  {Object.entries(v).map((subItem) => {
                    const subValue = subItem[1];
                    return (
                      <MenuItem
                        value={subValue}
                        key={subValue}
                        disabled={!policyTypes.includes(subValue)}
                      >
                        {t(`policy.addPolicy.options.${subValue}`)}
                      </MenuItem>
                    );
                  })}
                </SubmenuList>
              </Submenu>
            );
          }
        })}
      </MenuList>
    </Menu>
  );
}

function PolicyListItem({
  keyWord,
  policyName,
  policyItems,
  setModal,
  setRefreshTableTrigger,
  policyManualScanRunningQueue,
  overAllPolicyType,
  collectionType,
}: {
  keyWord: string;
  policyName: MailPolicyNameEnum | CollaborationPolicyNameEnum;
  policyItems: PolicyItemInTable[];
  setModal: ({ name, policy }: PolicyListModalState) => void;
  setRefreshTableTrigger: (trigger: Date) => void;
  policyManualScanRunningQueue: PolicyManualScanRunningQueue;
  overAllPolicyType: OverAllPolicyType;
  collectionType: ServiceCollection;
}) {
  const { t } = useTranslation();
  const [isExpanded, toggleExpand] = useToggle(true);
  const filteredPolicyItems =
    keyWord === ''
      ? policyItems
      : policyItems.filter((policy) =>
          [policy.policyName, policy.policyDescription, policy.policyTarget]
            .join(' ')
            .toLowerCase()
            .includes(keyWord.toLowerCase())
        );

  return (
    <Box pb={65}>
      <Flex align="center" pb={16}>
        <Text fontSize="20px">
          {t(`policy.table.policyName.${policyName}`)}&nbsp;(
          {filteredPolicyItems.length})
        </Text>
        <Space width="3x" />
        <IconButton onClick={toggleExpand}>
          {isExpanded ? (
            <Icon as={AngleDownIcon} />
          ) : (
            <Icon as={AngleRightIcon} />
          )}
        </IconButton>
      </Flex>
      <Box width="98%">
        {isExpanded && filteredPolicyItems.length > 0 && (
          <PolicyListTable
            policyType={filteredPolicyItems[0].policyType}
            setModal={setModal}
            data={filteredPolicyItems}
            setRefreshTableTrigger={setRefreshTableTrigger}
            policyManualScanRunningQueue={policyManualScanRunningQueue}
            overAllPolicyType={overAllPolicyType}
            collectionType={collectionType}
          />
        )}
      </Box>
    </Box>
  );
}
